// =============================================================================
// AI Model & Provider Configuration
// =============================================================================

export interface AIModel {
  id: string
  name: string
  description: string
  recommended?: boolean
}

export interface AIProvider {
  id: string
  name: string
  available: boolean
  icon: string
  models: AIModel[]
}

export const AI_PROVIDERS: AIProvider[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    available: true,
    icon: 'O',
    models: [
      { id: 'gpt-5-nano', name: 'GPT-5 Nano', description: 'Ultra Fast & Cheapest' },
      { id: 'gpt-5-mini', name: 'GPT-5 Mini', description: 'Fast & Cost-effective', recommended: true },
      { id: 'gpt-5', name: 'GPT-5', description: 'Balanced Performance' },
      { id: 'gpt-5.1', name: 'GPT-5.1', description: 'Advanced Reasoning' },
      { id: 'gpt-5.2', name: 'GPT-5.2', description: 'Latest & Most Capable' },
    ],
  },
  {
    id: 'google',
    name: 'Google',
    available: true,
    icon: 'G',
    models: [
      { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash', description: 'Fast & Efficient', recommended: true },
      { id: 'gemini-3.1-flash-lite-preview', name: 'Gemini 3.1 Flash Lite', description: 'Ultra Fast & Lightweight' },
      { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro', description: 'Advanced Reasoning' },
    ],
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    available: false,
    icon: 'A',
    models: [
      { id: 'claude-sonnet-4', name: 'Claude Sonnet 4', description: 'Fast & Intelligent' },
      { id: 'claude-opus-4', name: 'Claude Opus 4', description: 'Most Capable' },
    ],
  },
  {
    id: 'xai',
    name: 'xAI',
    available: false,
    icon: 'X',
    models: [
      { id: 'grok-3', name: 'Grok 3', description: 'Latest Generation' },
      { id: 'grok-3-mini', name: 'Grok 3 Mini', description: 'Fast & Efficient' },
    ],
  },
]

export interface ReasoningLevel {
  id: string
  name: string
  description: string
}

// Default reasoning levels (OpenAI/generic)
export const REASONING_LEVELS: ReasoningLevel[] = [
  { id: 'low', name: 'Low', description: 'Fastest responses' },
  { id: 'medium', name: 'Medium', description: 'Balanced speed & accuracy' },
  { id: 'high', name: 'High', description: 'Maximum accuracy' },
]

// Provider-specific reasoning levels
export const PROVIDER_REASONING_LEVELS: Record<string, ReasoningLevel[]> = {
  openai: [
    { id: 'low', name: 'Low', description: 'Fastest responses' },
    { id: 'medium', name: 'Medium', description: 'Balanced speed & accuracy' },
    { id: 'high', name: 'High', description: 'Maximum accuracy' },
  ],
  anthropic: [
    { id: 'low', name: 'Low', description: 'Fastest responses' },
    { id: 'medium', name: 'Medium', description: 'Balanced speed & accuracy' },
    { id: 'high', name: 'High', description: 'Maximum accuracy' },
  ],
  google: [
    { id: 'MINIMAL', name: 'Minimal', description: 'Fast & efficient' },
    { id: 'HIGH', name: 'Deep', description: 'Maximum reasoning' },
  ],
  xai: [
    { id: 'low', name: 'Low', description: 'Fastest responses' },
    { id: 'medium', name: 'Medium', description: 'Balanced speed & accuracy' },
    { id: 'high', name: 'High', description: 'Maximum accuracy' },
  ],
}

/**
 * Get available reasoning levels for a provider.
 */
export function getReasoningLevelsForProvider(providerId: string): ReasoningLevel[] {
  return PROVIDER_REASONING_LEVELS[providerId] || REASONING_LEVELS
}

/**
 * When switching providers, map the current reasoning to a valid option for the new provider.
 */
export function getDefaultReasoningForProvider(providerId: string, currentReasoning?: string): string {
  const levels = getReasoningLevelsForProvider(providerId)

  // If current reasoning exists in this provider's levels, keep it
  if (currentReasoning && levels.some(l => l.id === currentReasoning)) {
    return currentReasoning
  }

  // Map between Google ↔ OpenAI reasoning systems
  if (providerId === 'google') {
    return currentReasoning === 'high' ? 'HIGH' : 'MINIMAL'
  }

  // From Google to other providers
  if (currentReasoning === 'HIGH') return 'high'
  if (currentReasoning === 'MINIMAL') return 'low'

  return levels[0]?.id || 'low'
}

export function getProviderFromModel(modelId: string): string {
  if (modelId.startsWith('gpt-5')) return 'openai'
  if (modelId.startsWith('claude-')) return 'anthropic'
  if (modelId.startsWith('gemini-')) return 'google'
  if (modelId.startsWith('grok-')) return 'xai'
  return 'openai'
}

export function getProviderInfo(providerId: string): AIProvider | undefined {
  return AI_PROVIDERS.find((p) => p.id === providerId)
}

export function getModelInfo(modelId: string): AIModel | undefined {
  for (const provider of AI_PROVIDERS) {
    const model = provider.models.find((m) => m.id === modelId)
    if (model) return model
  }
  return undefined
}

// Provider styling config (using full Tailwind class names for JIT compatibility)
export const PROVIDER_STYLES: Record<string, {
  pill: string
  pillActive: string
  pillText: string
  pillActiveText: string
  ring: string
  badge: string
  dot: string
  gradient: string
  iconBg: string
  iconText: string
  glow: string
}> = {
  openai: {
    pill: 'border-gray-200 bg-white hover:bg-green-50 hover:border-green-200',
    pillActive: 'border-green-500 bg-green-50 shadow-sm',
    pillText: 'text-gray-600',
    pillActiveText: 'text-green-700 font-semibold',
    ring: 'ring-green-500 border-green-500',
    badge: 'bg-green-100 text-green-700',
    dot: 'bg-green-500',
    gradient: 'from-green-500 to-emerald-600',
    iconBg: 'bg-green-100',
    iconText: 'text-green-700',
    glow: 'shadow-green-200',
  },
  anthropic: {
    pill: 'border-gray-200 bg-white hover:bg-amber-50 hover:border-amber-200',
    pillActive: 'border-amber-500 bg-amber-50 shadow-sm',
    pillText: 'text-gray-600',
    pillActiveText: 'text-amber-700 font-semibold',
    ring: 'ring-amber-500 border-amber-500',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    gradient: 'from-amber-500 to-orange-600',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    glow: 'shadow-amber-200',
  },
  google: {
    pill: 'border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-200',
    pillActive: 'border-blue-500 bg-blue-50 shadow-sm',
    pillText: 'text-gray-600',
    pillActiveText: 'text-blue-700 font-semibold',
    ring: 'ring-blue-500 border-blue-500',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
    gradient: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-700',
    glow: 'shadow-blue-200',
  },
  xai: {
    pill: 'border-gray-200 bg-white hover:bg-purple-50 hover:border-purple-200',
    pillActive: 'border-purple-500 bg-purple-50 shadow-sm',
    pillText: 'text-gray-600',
    pillActiveText: 'text-purple-700 font-semibold',
    ring: 'ring-purple-500 border-purple-500',
    badge: 'bg-purple-100 text-purple-700',
    dot: 'bg-purple-500',
    gradient: 'from-purple-500 to-violet-600',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-700',
    glow: 'shadow-purple-200',
  },
}

export const REASONING_STYLES: Record<string, {
  card: string
  cardActive: string
  text: string
  textActive: string
  icon: string
}> = {
  low: {
    card: 'border-gray-200 bg-white hover:bg-emerald-50 hover:border-emerald-200',
    cardActive: 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm',
    text: 'text-gray-600',
    textActive: 'text-emerald-700',
    icon: 'text-emerald-500',
  },
  MINIMAL: {
    card: 'border-gray-200 bg-white hover:bg-sky-50 hover:border-sky-200',
    cardActive: 'border-sky-500 bg-gradient-to-br from-sky-50 to-blue-50 shadow-sm',
    text: 'text-gray-600',
    textActive: 'text-sky-700',
    icon: 'text-sky-500',
  },
  medium: {
    card: 'border-gray-200 bg-white hover:bg-amber-50 hover:border-amber-200',
    cardActive: 'border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-sm',
    text: 'text-gray-600',
    textActive: 'text-amber-700',
    icon: 'text-amber-500',
  },
  high: {
    card: 'border-gray-200 bg-white hover:bg-violet-50 hover:border-violet-200',
    cardActive: 'border-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 shadow-sm',
    text: 'text-gray-600',
    textActive: 'text-violet-700',
    icon: 'text-violet-500',
  },
  HIGH: {
    card: 'border-gray-200 bg-white hover:bg-indigo-50 hover:border-indigo-200',
    cardActive: 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-violet-50 shadow-sm',
    text: 'text-gray-600',
    textActive: 'text-indigo-700',
    icon: 'text-indigo-500',
  },
}
